import { useRef, useState } from 'react';
import { Icon } from '../../components/Icon';
import { PhotoPrepareError, preparePhoto, type PreparedPhoto } from './prepare';
import styles from './PhotoPicker.module.css';

export const MAX_PHOTOS = 4;

interface Props {
  photos: PreparedPhoto[];
  onChange: (photos: PreparedPhoto[]) => void;
  disabled?: boolean;
}

/** Picks up to four photos, prepares them on the device, and shows removable previews. */
export function PhotoPicker({ photos, onChange, disabled }: Props) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const room = MAX_PHOTOS - photos.length;

  const add = async (files: FileList | null) => {
    if (!files?.length) return;
    setError(null);
    const picked = Array.from(files).slice(0, room);
    if (files.length > room) setError(`A note can have up to ${MAX_PHOTOS} photos.`);
    setBusy(true);
    const prepared: PreparedPhoto[] = [];
    for (const file of picked) {
      try {
        prepared.push(await preparePhoto(file));
      } catch (e) {
        setError(e instanceof PhotoPrepareError ? e.message : "That photo couldn't be prepared.");
      }
    }
    setBusy(false);
    onChange([...photos, ...prepared]);
    if (input.current) input.current.value = '';
  };

  const remove = (index: number) => {
    URL.revokeObjectURL(photos[index]!.previewUrl);
    onChange(photos.filter((_, i) => i !== index));
  };

  return (
    <div className={styles.picker}>
      {photos.length > 0 && (
        <ul className={styles.previews} aria-label="Photos to post">
          {photos.map((photo, i) => (
            <li key={photo.previewUrl} className={styles.preview}>
              <img src={photo.previewUrl} alt={`Photo ${i + 1}`} />
              <button
                type="button"
                className={styles.remove}
                onClick={() => remove(i)}
                disabled={disabled}
                aria-label={`Remove photo ${i + 1}`}
              >
                <Icon name="close" size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {room > 0 && (
        <label className={[styles.add, (disabled || busy) && styles.addDisabled].filter(Boolean).join(' ')}>
          <input
            ref={input}
            type="file"
            accept="image/*"
            multiple
            className="visually-hidden"
            disabled={disabled || busy}
            onChange={(e) => add(e.target.files)}
          />
          <Icon name="camera" size={18} />
          {busy ? 'Preparing…' : photos.length ? 'Add another photo' : 'Add photos'}
        </label>
      )}
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
